import { useState } from 'react';

import { POST_SUBSCRIBE_REDIRECT_STORAGE_KEY } from 'pages/post-subscribe';

import {
    SlackChannel,
    SubscriptionBillingCycle,
    SubscriptionData,
    SubscriptionFromSubscriptionType,
    SubscriptionType,
} from 'models';

import { LocalNotificationService } from 'services/LocalNotificationService';
import { LocalStorageService } from 'services/LocalStorageService';
import { SlackNotificationService } from 'services/SlackNotificationService';
import { TeamService } from 'services/TeamService';

import { SubscriptionUpdateCommonProps } from '../types';

type UseSubmitSubscriptionUpdateParams<T extends SubscriptionType> = {
    toSubscription: SubscriptionFromSubscriptionType<T>;
    isDowngrade: boolean;
    quantity: number;
    additionalSubscriptions?: SubscriptionData[];
} & SubscriptionUpdateCommonProps;

export type SubscriptionUpdateState = {
    loading: boolean;
    legalChecked: boolean;
    legalError: boolean;
    billingCycle: SubscriptionBillingCycle;
};

export type SubscriptionUpdateStateController = {
    setLegalChecked: (value: boolean) => void;
    setLegalError: (value: boolean) => void;
    setBillingCycle: (value: SubscriptionBillingCycle) => void;
    submitSubscriptionUpdate: () => Promise<void>;
};

export const useSubscriptionUpdate = <T extends SubscriptionType>({
    toSubscription,
    defaultBillingCycle,
    quantity,
    additionalSubscriptions,
    isDowngrade,
    team,
    onClose,
    redirectUrl,
    postSubmitAction,
}: UseSubmitSubscriptionUpdateParams<T>): SubscriptionUpdateState & SubscriptionUpdateStateController => {
    const [loading, setLoading] = useState(false);
    const [legalError, setLegalError] = useState(false);
    const [legalChecked, setLegalChecked] = useState(false);
    const [billingCycle, setBillingCycle] = useState<SubscriptionBillingCycle>(defaultBillingCycle);

    const submitSubscriptionUpdate = async () => {
        const legalCheckRequired = !isDowngrade;
        if (legalCheckRequired && !legalChecked) {
            setLegalError(true);
            return;
        }

        setLoading(true);

        try {
            const { redirect, updatedTeam } = await TeamService.updateSubscription({
                teamId: team.id,
                subscriptions: [
                    {
                        subscription: toSubscription,
                        quantity,
                    },
                    ...(additionalSubscriptions ?? []),
                ],
                billingCycle,
            });

            if (postSubmitAction) {
                postSubmitAction();
            }

            if (redirect) {
                const returnUrl = new URL(redirectUrl || window.location.href, window.location.origin);
                returnUrl.searchParams.append('subscription', toSubscription);

                LocalStorageService.store(POST_SUBSCRIBE_REDIRECT_STORAGE_KEY, returnUrl.toString());

                window.location = redirect;
            } else if (updatedTeam) {
                onClose();

                LocalNotificationService.showSuccess({ message: 'Subscription updated successfully!' });

                TeamService.refresh(team.id);
            } else {
                throw new Error('Neither updated team nor redirect URL returned from subscription update');
            }
        } catch (error) {
            console.error('Error changing subscription', error);
            LocalNotificationService.showError({ message: 'Error changing subscription' });

            SlackNotificationService.send(
                [
                    '*User Encountered Error Changing Subscription*',
                    `Team: ${team.name} (${team.id})`,
                    `Subscription Upgrade Attempted: ${toSubscription}`,
                    `Quantity Attempted: ${quantity}`,
                    `Additional Subscriptions Attempted: ${additionalSubscriptions?.map((sub) => `${sub.subscription} (x${sub.quantity})`).join(', ')}`,
                    `Error message: ${JSON.stringify(error)}`,
                ].join('\n'),
                SlackChannel.PAYMENT,
            );
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        submitSubscriptionUpdate,
        legalChecked,
        legalError,
        setLegalChecked,
        setLegalError,
        billingCycle,
        setBillingCycle,
    };
};
